import { useState, useEffect, useLayoutEffect, useMemo } from "react";
import { usePrimaryWallet, useWalletList } from "@/hooks/useWalletQueries";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Droplet, Clock, TrendingUp, Wallet, AlertCircle } from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getFaucetConfigOptions, getFaucetStatsOptions, getFaucetStatusOptions, postClaimFaucetOptions } from "@/services/queryOptions/faucetQueryOptions";
import usePartner from "@/hooks/usePartner";
import { queryClient } from "@/lib/queryClient";
import { faucetKeys, walletKeys } from "@/constant/queryKeys";
import { useLocation } from "wouter";

export default function Faucet() {
  const { toast } = useToast();
  const { data: primaryWalletData } = usePrimaryWallet();
  const [, setLocation] = useLocation();
  const { data: walletList } = useWalletList()
  

  // Fetch faucet configuration based on active project's egg
  const { data: partner } = usePartner();
  const { data: stats, isLoading: statsLoading } = useQuery(getFaucetStatsOptions(partner?.id));
  const claimMutation = useMutation(postClaimFaucetOptions());
  const { data: config, isLoading: configLoading } = useQuery(getFaucetConfigOptions(partner?.id));
  
  const generatedWallet = useMemo(() => walletList?.wallets.find(data => data.walletType === 'generated'), [walletList?.wallets.length]);
  const walletAddress = config?.isOnlyEligibleForGeneratedWallet ? generatedWallet?.publicKey : primaryWalletData?.wallet.publicKey;
  const { data: status, isLoading: statusLoading } = useQuery(getFaucetStatusOptions(partner?.id, walletAddress));

  // Calculate min/max from config
  const minAmount = config?.minAmount || 50;
  const maxAmount = config?.maxAmount || 500;

  const [amount, setAmount] = useState(minAmount);
  const [countdown, setCountdown] = useState<string | null>(null);

  useLayoutEffect(function checkFaucetAvailibility() {
    if (!partner?.id) {
      return;
    }

    if (partner.hasFaucet) {
      return;
    }

    setLocation('/404');
  }, [partner?.id]);

  // Update amount when config changes
  useEffect(() => {
    setAmount(minAmount);
  }, [minAmount]);

  // Countdown timer effect
  useEffect(() => {
    if (!status?.nextClaimTime) {
      setCountdown(null);
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const nextClaim = new Date(status.nextClaimTime!);
      const diff = nextClaim.getTime() - now.getTime();

      if (diff <= 0) {
        setCountdown(null);
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown(`${hours}h ${minutes}m ${seconds}s`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [status?.nextClaimTime]);

  const handleClaim = async () => {
    if (!walletAddress) {
      toast({
        title: "No Wallet",
        description: "Please connect a wallet first",
        variant: "destructive",
      });
      return;
    }

    if (!partner?.id) {
      toast({
        title: "Invalid Project",
        description: "Please select a valid project",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await claimMutation.mutateAsync({
        partnerId: partner.id,
        walletAddress,
        amount,
      });

      queryClient.invalidateQueries({ queryKey: faucetKeys.status(partner.id, walletAddress) });
      queryClient.invalidateQueries({ queryKey: faucetKeys.stats() });
      queryClient.invalidateQueries({ queryKey: ['points'] });
      queryClient.invalidateQueries({ queryKey: walletKeys.tokenStats(config?.tokenName as string) })

      toast({
        title: "Success!",
        description: `${result.amount} tokens claimed successfully`,
      });
    } catch (error: any) {
      toast({
        title: "Claim Failed",
        description: error?.message || "Failed to claim tokens",
        variant: "destructive",
      });
    }
  };

  const canClaim = status?.remainingClaims && status.remainingClaims > 0;

  return (
    <main className="flex-grow">
      <div className="py-8 px-4 md:px-8 max-w-6xl mx-auto">
        <h2 className="text-3xl md:text-4xl mb-8 tiny5-font">TOKEN FAUCET</h2>

        {config?.isOnlyEligibleForGeneratedWallet && (
          <Alert className="mb-6 border-yellow-600 bg-yellow-900/20">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-200">
              Only generated wallets are eligible for this faucet. The address below is a generated wallet
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Claim Card */}
          <Card className="bg-card border-gray-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Droplet className="h-5 w-5 text-purple-500" />
                Claim Tokens
              </CardTitle>
              <CardDescription>
                Request test tokens for your wallet
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Wallet Display */}
              <div className="space-y-2">
                <label className="text-sm text-gray-400 flex items-center gap-2">
                  <Wallet className="h-4 w-4" />
                  Wallet Address
                </label>
                <div className="p-3 bg-secondary rounded-md font-mono text-sm truncate">
                  {walletAddress || "No wallet connected"}
                </div>
              </div>

              {/* Amount Slider */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label className="text-sm text-gray-400">Amount</label>
                  <span className="text-2xl font-bold text-purple-500">
                    {`${amount.toLocaleString()} ${config?.tokenName}` }
                  </span>
                </div>
                {configLoading ? (
                  <Skeleton className="h-6 w-full" />
                ) : (
                  <>
                    <Slider
                      value={[amount]}
                      onValueChange={(values) => setAmount(values[0])}
                      min={minAmount}
                      max={maxAmount}
                      step={minAmount ? Math.max(1, Math.floor(minAmount / 10)) : 10}
                      className="w-full"
                      disabled={!canClaim || claimMutation.isPending}
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Min: {minAmount.toLocaleString()}</span>
                      <span>Max: {maxAmount.toLocaleString()}</span>
                    </div>
                  </>
                )}
              </div>

              {/* Claim Status */}
              {statusLoading ? (
                <Skeleton className="h-20 w-full" />
              ) : (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Claims</span>
                    <span className="font-medium">{status?.claimsToday || 0} / {config?.maxClaims}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Remaining Claims</span>
                    <span className="font-medium text-purple-400">{status?.remainingClaims || 0}</span>
                  </div>
                </div>
              )}

              {/* Cooldown Timer */}
              {countdown && (
                <Alert className="border-blue-600 bg-blue-900/20">
                  <Clock className="h-4 w-4 text-blue-400" />
                  <AlertDescription className="text-blue-200">
                    Next claim available in: <span className="font-mono font-bold">{countdown}</span>
                  </AlertDescription>
                </Alert>
              )}

              {/* Claim Button */}
              <Button
                onClick={handleClaim}
                disabled={!canClaim || !walletAddress || claimMutation.isPending}
                className="w-full rounded-full px-6 py-6 text-lg font-medium border-solid border shadow-[inset_0px_0px_8px_0px_rgba(84,49,131,0.6),0px_0px_0px_2px_#0F1D4A] disabled:opacity-50"
                style={{
                    background: 'linear-gradient(0deg, #101D4A 0%, #873ED0 100%)',
                }}
                size="lg"
              >
                <span className="text-[#FFFFFF] font-['Inter'] font-semibold leading-[1.4] tracking-[0.16px] shadow-[_0px_3px_4px_0px_rgba(0,0,0,0.2)]">
                  {claimMutation.isPending ? "Claiming..." : `Claim ${amount.toLocaleString()} Tokens`}
                </span>
              </Button>

              {!canClaim && !countdown && (
                <p className="text-sm text-gray-400 text-center">
                  You've reached the maximum claims
                </p>
              )}
            </CardContent>
          </Card>

          {/* Statistics Card */}
          <Card className="bg-card border-gray-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-500" />
                Faucet Statistics
              </CardTitle>
              <CardDescription>
                Current faucet status and usage
              </CardDescription>
            </CardHeader>
            <CardContent>
              {statsLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Faucet Balance */}
                  <div className="p-4 bg-secondary rounded-lg">
                    <div className="text-sm text-gray-400 mb-1">Faucet Balance</div>
                    <div className="text-3xl font-bold text-green-400">
                      {stats?.faucetBalance?.toLocaleString() || "0"}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">tokens available</div>
                  </div>

                  {/* Total Distributed */}
                  <div className="p-4 bg-secondary rounded-lg">
                    <div className="text-sm text-gray-400 mb-1">Total Distributed</div>
                    <div className="text-3xl font-bold text-purple-400">
                      {stats?.totalDistributed?.toLocaleString() || "0"}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">tokens claimed</div>
                  </div>

                  {/* Total Claims */}
                  <div className="p-4 bg-secondary rounded-lg">
                    <div className="text-sm text-gray-400 mb-1">Total Claims</div>
                    <div className="text-3xl font-bold text-blue-400">
                      {stats?.totalClaims?.toLocaleString() || "0"}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">requests processed</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Info Section */}
        <Card className="bg-card border-gray-800">
          <CardHeader>
            <CardTitle>How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-gray-400">
              <li className="flex items-start gap-2">
                <span className="text-purple-500 mt-1">•</span>
                <span>
                  Each wallet can claim tokens {config?.maxClaims}
                  {(config?.maxClaims ?? 1) > 1 ? ' times' : ' time'} {config?.isDailyResetClaim && ' per 24-hour period'}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-500 mt-1">•</span>
                <span>Choose an amount between {minAmount} and {maxAmount} tokens per claim</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-500 mt-1">•</span>
                <span>Tokens are sent directly to your primary wallet address</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-500 mt-1">•</span>
                <span>Use these tokens to test minting features in development</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
